import { getStoreId } from '@utils/EnvironmentUtil'
import {
    CountMetric,
    LatencyMetric,
    AudioProcessingException,
    InvalidInputException,
    UnsupportedBrowserException,
} from '@amzn/ihm-associate-tools-js-lib'

/**
 * Constants used by the AudioProcessingService.
 */

const AudioProcessingServiceConstants = {
    // Lex needs 16000
    // Chrome can get down to 3000, FF around 8000.
    MIN_SAMPLE_RATE: 16000,
    // Chrome's maximum
    MAX_SAMPLE_RATE: 384000,
}

/**
 * Service that handles audio processing APIs.
 */

// Public functions

/**
 * Resamples an audio buffer to the targetSampleRate. This relies on OfflineAudioContext which wasn't supported by Safari
 * at the time of this writing. OfflineAudioContext runs in its own thread so this shouldn't block the main UI thread.
 *
 * @param {AudioBuffer} audioBuffer - The audioBuffer to resample.
 * @param {number} targetSampleRate - The sample rate that you're targeting. Note that the Max/Min values are Browser dependent.
 *  We do some validation against MIN_SAMPLE_RATE and MAX_SAMPLE_RATE but this doesn't mean that the browser that is running
 *  this will support it. For voice, it is supported in both FireFox and Chrome (and the Android WebView).
 *
 *  @return {Promise} Promise that resolves with the resampled audio or rejects if there's an error.
 */
export const resampleAudio = (audioBuffer, targetSampleRate) => {
    if (!audioBuffer) {
        return Promise.reject(new InvalidInputException({
            message: 'audioBuffer is a required argument',
        }))
    }

    if (!targetSampleRate || targetSampleRate < AudioProcessingServiceConstants.MIN_SAMPLE_RATE || targetSampleRate > AudioProcessingServiceConstants.MAX_SAMPLE_RATE) {
        return Promise.reject(new InvalidInputException({
            message: 'The sample rate must be between ' + AudioProcessingServiceConstants.MIN_SAMPLE_RATE + ' and ' + AudioProcessingServiceConstants.MAX_SAMPLE_RATE,
        }))
    }

    // Doesn't seem to be supported in Safari but it should be Ok
    if (!window.OfflineAudioContext) {
        new CountMetric(getStoreId(), 'Unsupported:OfflineAudioContext', 'IhmFlyingDutchman', 'audioProcessingService')
            .count(1)
        return Promise.reject(new UnsupportedBrowserException({
            message: 'OfflineAudioContext is not supported in this browser',
        }))
    }

    const channel = audioBuffer.numberOfChannels
    const audioBufferLength = audioBuffer.length
    const audioBufferSampleRate = audioBuffer.sampleRate

    // Length is the number of sample-frames in total for the buffer.
    // See {@link https://developer.mozilla.org/en-US/docs/Web/API/OfflineAudioContext/OfflineAudioContext#Parameters}
    const targetLength = audioBufferLength * targetSampleRate / audioBufferSampleRate

    console.debug(
        'Resampling audio... Original: numberOfChannels', channel, 'length', audioBufferLength, 'sampleRate', audioBufferSampleRate,
        'New length', targetLength,
    )

    let offlineContext

    const resampleFaultMetric = new CountMetric(getStoreId(), 'Fault', 'IhmFlyingDutchman', 'audioProcessingService.resampleAudio')
    const resampleAudioLatencyMetric = new LatencyMetric(getStoreId(), 'Time', 'IhmFlyingDutchman', 'audioProcessingService.resampleAudio')
    resampleAudioLatencyMetric.start()

    try {
        offlineContext = new window.OfflineAudioContext(channel, targetLength, targetSampleRate)
    } catch (error) {
        console.warn('Could not initialize OfflineAudioContext with channel', channel, 'targetLength', targetLength, 'sampleRate', targetSampleRate, error)
        new CountMetric(getStoreId(), 'Unsupported:OfflineAudioContext-runtime', 'IhmFlyingDutchman', 'audioProcessingService')
            .count(1)
        return Promise.reject(new UnsupportedBrowserException({
            message: 'The browser does not support a sample rate of ' + targetSampleRate,
        }))
    }

    // We do this to make sure that all is wrapped in a promise and can be reliably caught in a
    // .catch clause
    return Promise.resolve()
        .then(() => {
            const bufferSource = offlineContext.createBufferSource()
            bufferSource.buffer = audioBuffer

            bufferSource.connect(offlineContext.destination)
            bufferSource.start(0)

            // This returns a native promise but it is wrapped automatically
            return offlineContext.startRendering()
        })
        .then((result) => {
            resampleAudioLatencyMetric.stop()
            resampleFaultMetric.count(0)
            return result
        })
        .catch((error) => {
            resampleFaultMetric.count(1)
            console.warn('Could not initialize OfflineAudioContext with channel', channel, 'targetLength', targetLength, 'sampleRate', targetSampleRate, error)
            return Promise.reject(new AudioProcessingException({
                message: 'Error resampling the audio',
            }))
        })
}

/**
 * Converts a buffer of Float32 to Int16 by normalizing the values.
 *
 * @param {Float32Array} buffer - A buffer of Float32 values expected to be in range from -1 to 1.
 *
 * @return {Uint16Array} Values are normalized and have values between -32767 to 32767.
 */
export const convertFloat32ToInt16 = (buffer) => {
    const len = buffer.length
    const newBuffer = new Int16Array(len)
    for (let i = 0; i < len; i++) {
        newBuffer[i] = Math.max(Math.min(1, buffer[i]), -1) * 0x7FFF
    }
    return newBuffer
}
